import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Card, CardContent } from '@/components/ui/card';
import { apiManagerApi } from '../../api-manager/api';
import { CREDENTIAL_STATE_LABEL, type CredentialState } from '../../api-manager/apiRegistryView';

type PanelRow = {
  api_id: string;
  api_name: string;
  provider: string | null;
  status: string | null;
  visibility: 'shared' | 'private';
  sharing: string | null;
  credential_state: CredentialState;
  consent_count: number;
  pipelines: Array<{ pipeline_id: string; last_run_at: string | null }>;
};

export function AppApiConnectionsCard({ appId }: { appId: string }) {
  const query = useQuery({
    queryKey: ['app-connection-panel', appId],
    queryFn: async () => {
      const response = await apiManagerApi.listAppConnectionPanel(appId);
      return (response.data?.connections || []) as PanelRow[];
    },
    retry: false,
    enabled: Boolean(appId),
  });

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">API connections</h2>
          <div className="flex gap-3 text-sm">
            <Link to={`/api-ingest?app_id=${encodeURIComponent(appId)}`}>API Ingest</Link>
            <Link to={`/api-observe?app_id=${encodeURIComponent(appId)}`}>API Observe</Link>
          </div>
        </div>
        {query.isLoading ? <p className="text-sm">Loading connections.</p> : null}
        {query.isError ? (
          <p className="text-sm">
            {(query.error as { response?: { status?: number } })?.response?.status === 403
              ? "You don't have access to this app's connections."
              : 'Connections could not be loaded.'}
          </p>
        ) : null}
        {query.data && query.data.length === 0 ? <p className="text-sm">No API connections for this app.</p> : null}
        <ul className="space-y-2">
          {(query.data || []).map((row) => (
            <li key={row.api_id} className="rounded border p-3 text-sm">
              <div className="font-medium">{row.api_name}</div>
              <div>{row.provider || 'Provider not set'} · {row.visibility} · {row.status || 'status not set'}</div>
              {row.sharing ? <div>{row.sharing}</div> : null}
              <div>{CREDENTIAL_STATE_LABEL[row.credential_state] || 'Missing'} · {row.consent_count} consents</div>
              <ul>
                {row.pipelines.map((pipe) => (
                  <li key={pipe.pipeline_id}>
                    {pipe.pipeline_id} · last run {pipe.last_run_at || 'never'}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
